import Vue from "vue";
import VueRouter from "vue-router";
import jwt_decode from "jwt-decode";
import Home from "@/views/Home.vue";
import Login from "@/views/Login.vue";

//import ServiceList from "@/views/services/ServiceList.vue";
//import TollList from "@/views/services/TollList.vue";

//import PassengerList from "@/views/customer/PassengerList.vue";
//import CustomerList from "@/views/customer/CustomerList.vue";
//import RequesterList from "@/views/customer/RequesterList.vue";

//import PaymentTotemList from "@/views/payment-totem/PaymentTotemList.vue";
//import PaymentTotemDetails from "@/views/payment-totem/PaymentTotemDetails.vue";

import AdminList from "@/views/admin/AdminList.vue";
import AdminDetails from "@/views/admin/AdminDetails.vue";
import RoleList from "@/views/role/RoleList.vue";
import RoleDetails from "@/views/role/RoleDetails.vue";
import TenantList from "@/views/tenant/TenantList.vue";
import TenantDetails from "@/views/tenant/TenantDetails.vue";

import EmployeeList from "@/views/employee/EmployeeList.vue";
import EmployeeDetails from "@/views/employee/EmployeeDetails.vue";

import StudentList from "@/views/student/StudentList.vue";
import StudentDetails from "@/views/student/StudentDetails.vue";
import StudentSummary from "@/views/student/StudentSummary.vue";

import AnswerableList from "@/views/answerable/AnswerableList.vue";
import AnswerableDetails from "@/views/answerable/AnswerableDetails.vue";

import EquipmentList from "@/views/equipment/EquipmentList.vue";
import EquipmentDetails from "@/views/equipment/EquipmentDetails.vue";

import OccurrenceList from "@/views/occurrence/OccurrenceList.vue";
import OccurrenceDetails from "@/views/occurrence/OccurrenceDetails.vue";

import AnnouncementList from "@/views/announcement/AnnouncementList.vue";
import AnnouncementDetails from "@/views/announcement/AnnouncementDetails.vue";
import ChatList from "@/views/chat/ChatList.vue";
import ChatDetails from "@/views/chat/ChatDetails.vue";

import EducationList from "@/views/education/EducationList.vue";
import EducationDetails from "@/views/education/EducationDetails.vue";

import ClassList from "@/views/class/ClassList.vue";
import ClassDetails from "@/views/class/ClassDetails.vue";
import ClassSummary from "@/views/class/ClassSummary.vue";

import ClassRoomList from "@/views/classroom/ClassRoomList.vue";
import ClassRoomDetails from "@/views/classroom/ClassRoomDetails.vue";

import CourseList from "@/views/course/CourseList.vue";
import CourseDetails from "@/views/course/CourseDetails.vue";
import AssessmentList from "@/views/assessment/AssessmentList.vue";
import AssessmentDetails from "@/views/assessment/AssessmentDetails.vue";
import AttendanceList from "@/views/attendance/AttendanceList.vue";

import SchoolYearList from "@/views/school-year/SchoolYearList.vue";
import SchoolYearDetails from "@/views/school-year/SchoolYearDetails.vue";

import SchoolEnrollmentList from "@/views/school-enrollment/SchoolEnrollmentList.vue";
import SchoolEnrollmentDetails from "@/views/school-enrollment/SchoolEnrollmentDetails.vue";

import AcademicPeriodList from "@/views/academic-period/AcademicPeriodList.vue";
import AcademicPeriodDetails from "@/views/academic-period/AcademicPeriodDetails.vue";

import SchoolTransporterList from "@/views/school-transporter/SchoolTransporterList.vue";
import SchoolTransporterDetails from "@/views/school-transporter/SchoolTransporterDetails.vue";

import GuestList from "@/views/guest/GuestList.vue";
import GuestDetails from "@/views/guest/GuestDetails.vue";

import Calendar from "@/views/calendar/Calendar.vue"
import SchoolExitPanel from "@/views/school-exit/SchoolExitPanel.vue";
import SchoolExitSettings from "@/views/school-exit/SchoolExitSettings.vue";

const UserType = {
  ANSWERABLE: 1,
  ADMIN: 2,
  STUDENT: 3,
  TEACHER: 4,
  MONITOR: 5,
  COORDINATOR: 6,
  TRANSPORTER: 7,
  GUEST: 8,
  EMPLOYEE: 9,
  MASTER_ADMIN: 99,
};

const routePermissions: { [key: number]: string[] } = {
  [UserType.MASTER_ADMIN]: ["Home", "Tenant"],
  [UserType.ADMIN]: ["*"],
  [UserType.TEACHER]: ["Home", "Student", "Assessment", "Attendance", "Grade", "Class", "Course", "AcademicPeriod", "Chat"],
  [UserType.MONITOR]: ["Home", "Student", "Attendance", "Occurrence", "Chat", "SchoolExit"],
  [UserType.COORDINATOR]: ["Home", "Student", "Class", "Occurrence", "Announcement", "Chat"],
};

const profileTypeMap: { [key: string]: number } = {
  master_admin: UserType.MASTER_ADMIN,
  admin: UserType.ADMIN,
  teacher: UserType.TEACHER,
  monitor: UserType.MONITOR,
};

function normalizeUserPayload(payload: any) {
  let data = payload || {};

  for (let index = 0; index < 3; index += 1) {
    const nested = data && data.data;
    const nestedHasUserShape = nested && (
      nested.type ||
      nested.current_profile ||
      nested.profiles ||
      nested.profile_types ||
      nested.profile ||
      nested.role
    );

    if (nestedHasUserShape || (data && !data.type && !data.current_profile && !data.profiles && data.data)) {
      data = data.data;
    }
  }

  return data || {};
}

function readStoredUser() {
  try {
    const stored = JSON.parse(localStorage.getItem("user") || "{}");
    return normalizeUserPayload(stored);
  } catch (error) {
    return {};
  }
}

function readTokenUser() {
  try {
    const token = JSON.parse(localStorage.getItem("token") || "null");
    if (!token) return {};

    return normalizeUserPayload(jwt_decode(token));
  } catch (error) {
    return {};
  }
}

function resolveUserTypes() {
  const users = [readStoredUser(), readTokenUser()];
  const types = [
    ...users.flatMap((user) => [
      user.type,
      user.current_profile && user.current_profile.type,
      ...((user.profiles || []).map((profile: any) => profile.type)),
      ...((user.profile_types || []).map((profileType: string) => profileTypeMap[profileType])),
      user.profile ? UserType.ADMIN : null,
      user.role ? UserType.ADMIN : null,
    ]),
  ]
    .map((type) => Number(type))
    .filter(Boolean);

  return [...new Set(types)];
}

function resolveUserPermissions() {
  const users = [readStoredUser(), readTokenUser()];
  const permissions = users.flatMap((user) => [
    ...((user.permissions || [])),
    ...((user.current_profile && user.current_profile.permissions) || []),
    ...((user.profiles || []).flatMap((profile: any) => profile.permissions || [])),
  ]);

  return permissions;
}

function routePermissionMatches(permission: any, routeName: string) {
  if (permission === "*") return true;
  if (typeof permission === "string") return permission === routeName;
  return permission.path === routeName || permission.path === "*";
}

function canAccessRoute(routeName: string | null | undefined) {
  if (!routeName || routeName === "login") return true;

  const userTypes = resolveUserTypes();
  const userPermissions = resolveUserPermissions();

  if (routeName === "Tenant") return userTypes.includes(UserType.MASTER_ADMIN);

  if (userPermissions.length) return userPermissions.some((permission) => routePermissionMatches(permission, routeName));

  const hasExplicitPermission = userTypes.some((userType) => {
    const permissions = routePermissions[userType] || [];
    return permissions.includes("*") || permissions.includes(routeName);
  });

  if (hasExplicitPermission) return true;

  return false;
}


const guard = function (to: any, from: any, next: any) {
  const token = localStorage.getItem("token");

  if (!token && to.path !== "/login") {
    next({
      path: "/login",
    });
  } else {

    if (to.name == "Home") {
      next();
      return;
    }

    if (!canAccessRoute(to.meta && to.meta.permission ? to.meta.permission : to.name)) {
      next({ path: "/home" });
      return;
    }

    next();
  }
};

Vue.use(VueRouter);

const routes = [
  {
    path: "/",
    component: () => import("@/layouts/Public.vue"),
    children: [
      {
        path: "/login",
        name: "login",
        component: Login,
      },
      {
        path: "/",
        redirect: "/login",
      },
    ],
  },
  {
    path: "/",
    component: () => import("@/layouts/Private.vue"),
    beforeEnter: guard,
    children: [
      {
        path: "/home",
        name: "Home",
        component: Home,
        beforeEnter: guard,
      },
      {
        path: "/tenants",
        name: "Tenant",
        component: TenantList,
        beforeEnter: guard,
      },
      {
        path: "/tenants/:id",
        name: "TenantDetails",
        meta: { permission: "Tenant" },
        component: TenantDetails,
        beforeEnter: guard,
      },
      {
        path: "/admins",
        name: "Admin",
        component: AdminList,
        beforeEnter: guard,
      },
      {
        path: "/admins/:id",
        name: "AdminDetails",
        meta: { permission: "Admin" },
        component: AdminDetails,
        beforeEnter: guard,
      },
      {
        path: "/cargos",
        name: "Role",
        component: RoleList,
        beforeEnter: guard,
      },
      {
        path: "/cargos/:id",
        name: "RoleDetails",
        meta: { permission: "Role" },
        component: RoleDetails,
        beforeEnter: guard,
      },
      {
        path: "/colaboradores",
        name: "Employee",
        component: EmployeeList,
        beforeEnter: guard,
      },
      {
        path: "/colaboradores/:id",
        name: "EmployeeDetails",
        meta: { permission: "Employee" },
        component: EmployeeDetails,
        beforeEnter: guard,
      },
      {
        path: "/alunos",
        name: "Student",
        component: StudentList,
        beforeEnter: guard,
      },
      {
        path: "/alunos/:id/resumo",
        name: "StudentSummary",
        meta: { permission: "Student" },
        component: StudentSummary,
        beforeEnter: guard,
      },
      {
        path: "/alunos/:id",
        name: "StudentDetails",
        meta: { permission: "Student" },
        component: StudentDetails,
        beforeEnter: guard,
      },
      {
        path: "/responsaveis",
        name: "Answerable",
        component: AnswerableList,
        beforeEnter: guard,
      },
      {
        path: "/responsaveis/:id",
        name: "AnswerableDetails",
        meta: { permission: "Answerable" },
        component: AnswerableDetails,
        beforeEnter: guard,
      },
      {
        path: "/ensinos",
        name: "Education",
        component: EducationList,
        beforeEnter: guard,
      },
      {
        path: "/ensinos/:id",
        name: "EducationDetails",
        meta: { permission: "Education" },
        component: EducationDetails,
        beforeEnter: guard,
      },
      {
        path: "/equipamentos",
        name: "Equipment",
        component: EquipmentList,
        beforeEnter: guard,
      },
      {
        path: "/equipamentos/:id",
        name: "EquipmentDetails",
        meta: { permission: "Equipment" },
        component: EquipmentDetails,
        beforeEnter: guard,
      },
      {
        path: "/painel-saida",
        name: "SchoolExit",
        component: SchoolExitPanel,
        beforeEnter: guard,
      },
      {
        path: "/configuracoes-escola",
        name: "SchoolSetting",
        component: SchoolExitSettings,
        beforeEnter: guard,
      },
      {
        path: "/configuracao-saida",
        redirect: "/configuracoes-escola",
      },
      {
        path: "/ocorrencias",
        name: "Occurrence",
        component: OccurrenceList,
        beforeEnter: guard,
      },
      {
        path: "/ocorrencias/:id",
        name: "OccurrenceDetails",
        meta: { permission: "Occurrence" },
        component: OccurrenceDetails,
        beforeEnter: guard,
      },
      {
        path: "/comunicados",
        name: "Announcement",
        component: AnnouncementList,
        beforeEnter: guard,
      },
      {
        path: "/comunicados/:id",
        name: "AnnouncementDetails",
        meta: { permission: "Announcement" },
        component: AnnouncementDetails,
        beforeEnter: guard,
      },
      {
        path: "/conversas",
        name: "Chat",
        component: ChatList,
        beforeEnter: guard,
      },
      {
        path: "/conversas/:id",
        name: "ChatDetails",
        meta: { permission: "Chat" },
        component: ChatDetails,
        beforeEnter: guard,
      },
      {
        path: "/turmas",
        name: "Class",
        component: ClassList,
        beforeEnter: guard,
      },
      {
        path: "/turmas/:id/resumo",
        name: "ClassSummary",
        meta: { permission: "Class" },
        component: ClassSummary,
        beforeEnter: guard,
      },
      {
        path: "/turmas/:id",
        name: "ClassDetails",
        meta: { permission: "Class" },
        component: ClassDetails,
        beforeEnter: guard,
      },
      {
        path: "/salas-aula",
        name: "Classroom",
        component: ClassRoomList,
        beforeEnter: guard,
      },
      {
        path: "/salas-aula/:id",
        name: "ClassroomDetails",
        meta: { permission: "Classroom" },
        component: ClassRoomDetails,
        beforeEnter: guard,
      },
      {
        path: "/disciplinas",
        name: "Course",
        component: CourseList,
        beforeEnter: guard,
      },
      {
        path: "/disciplinas/:id",
        name: "CourseDetails",
        meta: { permission: "Course" },
        component: CourseDetails,
        beforeEnter: guard,
      },
      {
        path: "/avaliacoes",
        name: "Assessment",
        component: AssessmentList,
        beforeEnter: guard,
      },
      {
        path: "/avaliacoes/:id",
        name: "AssessmentDetails",
        meta: { permission: "Assessment" },
        component: AssessmentDetails,
        beforeEnter: guard,
      },
      {
        path: "/diario-classe",
        name: "Attendance",
        component: AttendanceList,
        beforeEnter: guard,
      },
      {
        path: "/anos-escolares",
        name: "SchoolYear",
        component: SchoolYearList,
        beforeEnter: guard,
      },
      {
        path: "/anos-escolares/:id",
        name: "SchoolYearDetails",
        meta: { permission: "SchoolYear" },
        component: SchoolYearDetails,
        beforeEnter: guard,
      },
      {
        path: "/matriculas",
        name: "SchoolEnrollment",
        component: SchoolEnrollmentList,
        beforeEnter: guard,
      },
      {
        path: "/matriculas/:id",
        name: "SchoolEnrollmentDetails",
        meta: { permission: "SchoolEnrollment" },
        component: SchoolEnrollmentDetails,
        beforeEnter: guard,
      },
      {
        path: "/periodos-academicos",
        name: "AcademicPeriod",
        component: AcademicPeriodList,
        beforeEnter: guard,
      },
      {
        path: "/periodos-academicos/:id",
        name: "AcademicPeriodDetails",
        meta: { permission: "AcademicPeriod" },
        component: AcademicPeriodDetails,
        beforeEnter: guard,
      },
      {
        path: "/transportes-escolares",
        name: "SchoolTransporter",
        component: SchoolTransporterList,
        beforeEnter: guard,
      },
      {
        path: "/transportes-escolares/:id",
        name: "SchoolTransporterDetails",
        meta: { permission: "SchoolTransporter" },
        component: SchoolTransporterDetails,
        beforeEnter: guard,
      },
      {
        path: "/visitantes",
        name: "Guest",
        component: GuestList,
        beforeEnter: guard,
      },
      {
        path: "/visitantes/:id",
        name: "GuestDetails",
        meta: { permission: "Guest" },
        component: GuestDetails,
        beforeEnter: guard,
      },
      {
        path: "/eventos",
        name: "Calendar",
        component: Calendar,
        beforeEnter: guard,
      },
      // {
      //   path: "/totens/:id",
      //   name: "PaymentTotemDetails",
      //   component: PaymentTotemDetails,
      //   beforeEnter: guard,
      // },
    ],
  },
];

const router = new VueRouter({
  base: process.env.BASE_URL,
  routes,
});

export default router;
